import { useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import queryString, {
  exclude,
  parse,
  ParsedQuery,
  stringifyUrl,
} from 'query-string'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import log from 'loglevel'
import {
  AiGrcQaAbsoluteRoutes,
  ControlsAbsoluteRoutes,
  DirectRoutes,
  EvidenceAbsoluteRoutes,
  ExamineAndTestAbsoluteRoutes,
  PoliciesAbsoluteRoutes,
  ReceptorsAbsoluteRoutes,
  RequestsAbsoluteRoutes,
  RisksAbsoluteRoutes,
  ScopeAbsoluteRoutes,
  ServicesAbsoluteRoutes,
  TrusteroIntelligenceAbsoluteRoutes,
  VendorsAbsoluteRoutes,
} from '../../Reusable/RootPage/RootPage.constants'

interface GeneratePermalinkArgs {
  pageContext: string
  modelType: MODEL_TYPE
  modelId: string
  isInternalLink: boolean
  includeSearchParams?: boolean
  search?: ParsedQuery<string>
}

const safeStringifyUrl = (url: string, query?: ParsedQuery<string>): string =>
  stringifyUrl({ url, query }, { arrayFormat: 'bracket', encode: false })

export const linkWithSearch = (
  link: string,
  search?: ParsedQuery<string>,
  includeHash?: boolean,
): string =>
  `${safeStringifyUrl(
    link,
    search
      ? search
      : location.search.toString()
      ? parse(location.search, {
          arrayFormat: 'bracket',
        })
      : undefined,
  )}${includeHash ? location.hash : ''}`

export const getLinkWithSearchAndExclude = (
  link: string,
  search?: ParsedQuery<string>,
  includeHash?: boolean,
  excludeParams?: string[],
): string => {
  const searchParams = search
    ? search
    : location.search.toString()
    ? parse(location.search, {
        arrayFormat: 'bracket',
      })
    : undefined

  const newLink = excludeParams
    ? exclude(linkWithSearch(link, searchParams, includeHash), excludeParams)
    : linkWithSearch(link, searchParams, includeHash)
  return newLink
}

export const useNavigateWithSearch = (): ((link: string) => void) => {
  const navigate = useNavigate()

  return useCallback(
    (link: string) => navigate(linkWithSearch(link)),
    [navigate],
  )
}

export const generatePermalink = ({
  pageContext,
  modelType,
  modelId,
  isInternalLink,
  includeSearchParams,
  search,
}: GeneratePermalinkArgs): string => {
  const origin = isInternalLink
    ? `/${pageContext}`
    : `${window.location.origin}/${pageContext}`

  let baseLink: string

  switch (modelType) {
    case MODEL_TYPE.POLICY:
      if (modelId) {
        baseLink = `${origin}/${PoliciesAbsoluteRoutes.SHOW}/${modelId}`
      } else {
        baseLink = `${origin}/${PoliciesAbsoluteRoutes.INDEX_PAGE}`
      }
      break
    case MODEL_TYPE.CONTROL:
      if (modelId) {
        baseLink = `${origin}/${ControlsAbsoluteRoutes.SHOW}/${modelId}`
      } else {
        baseLink = `${origin}/${ControlsAbsoluteRoutes.INDEX_PAGE}`
      }
      break
    case MODEL_TYPE.OVERVIEW:
      baseLink = `${origin}/company_info`
      break
    case MODEL_TYPE.RISK:
      if (modelId) {
        baseLink = `${origin}/${RisksAbsoluteRoutes.SHOW}/${modelId}`
      } else {
        baseLink = `${origin}/${RisksAbsoluteRoutes.INDEX}`
      }
      break
    case MODEL_TYPE.AUDITBOT_RUN:
      if (modelId) {
        baseLink = `${origin}/${ExamineAndTestAbsoluteRoutes.SHOW}/${modelId}`
      } else {
        baseLink = `${origin}/${ExamineAndTestAbsoluteRoutes.INDEX}`
      }
      break
    case MODEL_TYPE.SERVICE:
      baseLink = `${origin}/${ServicesAbsoluteRoutes.SHOW}/${modelId}`
      break
    case MODEL_TYPE.RECEPTOROBJ:
      if (modelId) {
        baseLink = `${origin}/${ReceptorsAbsoluteRoutes.SHOW}/${modelId}`
      } else {
        baseLink = `${origin}/${ReceptorsAbsoluteRoutes.INDEX}`
      }
      break
    case MODEL_TYPE.QUESTIONNAIRE:
      baseLink = `${origin}/${AiGrcQaAbsoluteRoutes.SHOW}/${modelId}`
      break
    case MODEL_TYPE.VENDOR:
      if (modelId) {
        baseLink = `${origin}/${VendorsAbsoluteRoutes.SHOW}/${modelId}`
      } else {
        baseLink = `${origin}/${VendorsAbsoluteRoutes.INDEX}`
      }
      break
    case MODEL_TYPE.EVIDENCE:
      baseLink = `${origin}/${EvidenceAbsoluteRoutes.SHOW}/${modelId}`
      break
    case MODEL_TYPE.REQUEST:
      baseLink = `${origin}/requests/show/${modelId}`
      break
    case MODEL_TYPE.CHAT:
      baseLink = `${origin}/${TrusteroIntelligenceAbsoluteRoutes.SHOW}/${modelId}`
      break
    case MODEL_TYPE.SCOPE:
      baseLink = `${origin}/${ScopeAbsoluteRoutes.ROOT}`
      break
    case MODEL_TYPE.ALL_SERVICES:
      baseLink = `${origin}/${ServicesAbsoluteRoutes.INDEX}`
      break
    case MODEL_TYPE.DMS:
      baseLink = `${origin}/${DirectRoutes.MANAGE_DOCUMENTS}`
      break
    case MODEL_TYPE.USEROBJ:
      baseLink = `${origin}/${DirectRoutes.USERS}`
      break
    case MODEL_TYPE.AUDIT:
      baseLink = `${origin}/${DirectRoutes.AUDITS}`
      break
    case MODEL_TYPE.DOCUMENT_MANAGEMENT:
      baseLink = `${origin}/${DirectRoutes.DOCUMENT_MANAGEMENT}`
      break
    case MODEL_TYPE.REPORT:
      if (modelId) {
        baseLink = `${origin}/${DirectRoutes.REPORTS}/show/${modelId}`
      } else {
        baseLink = `${origin}/${DirectRoutes.REPORTS}`
      }
      break
    default:
      baseLink = origin
  }

  return includeSearchParams ? linkWithSearch(baseLink, search) : baseLink
}

// if auditId is not passed in, this will generate permalink for continuous
// compliance view
export const generateAuditPermalink = ({
  pageContext,
  overrideAuditId,
  isInternalLink,
  destination,
}: {
  pageContext?: string
  overrideAuditId?: string
  isInternalLink: boolean
  destination: string
}): string => {
  const environment = (pageContext ?? '').split(':')[0]

  let origin = isInternalLink ? '/' : `${window.location.origin}`

  origin += environment
  if (overrideAuditId) {
    origin += `:${overrideAuditId}`
  }
  origin += `/${destination}`

  return origin
}

/**
 * Generates a permalink from a ID.
 * @param {string} pageContext - The page context.
 * @param {string} modelId - This can be the modelID or any ID.
 * @param {boolean} isInternalLink - Whether the link is internal or not.
 * @returns {string} The generated permalink.
 */
export const generatePermalinkFromId = ({
  pageContext,
  modelId,
  modelType,
  isInternalLink,
  includeSearchParams,
  search,
}: GeneratePermalinkArgs): string => {
  const linkModelType = modelType
  return generatePermalink({
    pageContext,
    modelType: linkModelType,
    modelId,
    isInternalLink,
    includeSearchParams,
    search,
  })
}

export const generateTestPermalinkFromId = ({
  pageContext,
  controlId,
  testId,
  isInternalLink,
}: {
  pageContext: string
  controlId: string
  testId: string
  isInternalLink: boolean
}): string => {
  const origin = isInternalLink
    ? `/${pageContext}`
    : `${window.location.origin}/${pageContext}`

  const url = `/${ControlsAbsoluteRoutes.SHOW}/${controlId}/test/${testId}`

  return origin + url
}

export const generatePermalinkFromCurrentLocation = (): string => {
  return window.location.toString()
}

export const openLinkInNewTab = (url: string): void => {
  const newWindow = window.open(url, '_blank')
  if (newWindow) {
    newWindow.focus()
  } else {
    log.error('Failed to open new window')
  }
}

export const useScopePermalink = (): string => {
  const params = useParams()
  return generatePermalink({
    pageContext: params.pageContext as string,
    modelType: MODEL_TYPE.SCOPE,
    modelId: '',
    isInternalLink: false,
  })
}

export const useServicesPermalink = (): string => {
  const params = useParams()
  return generatePermalink({
    pageContext: params.pageContext as string,
    modelType: MODEL_TYPE.ALL_SERVICES,
    modelId: '',
    isInternalLink: false,
  })
}

export const useCompanyInfoPermalink = (): string => {
  const params = useParams()
  return generatePermalink({
    pageContext: params.pageContext as string,
    modelType: MODEL_TYPE.OVERVIEW,
    modelId: '',
    isInternalLink: false,
  })
}

export const useDocumentManagementPermalink = (): string => {
  const params = useParams()
  return generatePermalink({
    pageContext: params.pageContext as string,
    modelType: MODEL_TYPE.DMS,
    modelId: '',
    isInternalLink: false,
  })
}

export const useAuditsPermalink = (): string => {
  const params = useParams()
  return generatePermalink({
    pageContext: params.pageContext as string,
    modelType: MODEL_TYPE.AUDIT,
    modelId: '',
    isInternalLink: false,
  })
}

export const useReceptorsPermalink = (): string => {
  const params = useParams()
  return generatePermalink({
    pageContext: params.pageContext as string,
    modelType: MODEL_TYPE.RECEPTOROBJ,
    modelId: '',
    isInternalLink: false,
  })
}

export const useRiskRegisterPermalink = (): string => {
  const params = useParams()
  return generatePermalink({
    pageContext: params.pageContext as string,
    modelType: MODEL_TYPE.RISK,
    modelId: '',
    isInternalLink: false,
  })
}

export const useSoc2ReportSummaryPermalink = (): string => {
  const params = useParams()
  return generatePermalink({
    pageContext: params.pageContext as string,
    modelType: MODEL_TYPE.SOC2_REPORT_SUMMARY,
    modelId: '',
    isInternalLink: false,
  })
}

export const useVendorsPermalink = (): string => {
  const params = useParams()
  return generatePermalink({
    pageContext: params.pageContext as string,
    modelType: MODEL_TYPE.VENDOR,
    modelId: '',
    isInternalLink: false,
  })
}

export const useUsersPermalink = (): string => {
  const params = useParams()
  return generatePermalink({
    pageContext: params.pageContext as string,
    modelType: MODEL_TYPE.USEROBJ,
    modelId: '',
    isInternalLink: false,
  })
}

export const useReportPermalink = (
  reportId?: string,
  isInternalLink = false,
): string => {
  const params = useParams()
  return generatePermalink({
    pageContext: params.pageContext as string,
    modelType: MODEL_TYPE.REPORT,
    modelId: reportId ?? '',
    isInternalLink,
  })
}

export const useRequestsPermalink = (query?: ParsedQuery<string>): string => {
  const params = useParams()
  const base = generateAuditPermalink({
    pageContext: params.pageContext as string,
    isInternalLink: true,
    destination: RequestsAbsoluteRoutes.INDEX,
  })
  return query ? safeStringifyUrl(base, query) : base
}

export const useReportChatPermalink = ({
  chatId,
  reportId,
  isInternalLink = false,
}: {
  chatId: string
  reportId: string
  isInternalLink?: boolean
}): string => {
  const params = useParams()
  return generatePermalink({
    pageContext: params.pageContext as string,
    modelType: MODEL_TYPE.CHAT,
    modelId: chatId,
    isInternalLink,
    search: queryString.parse(`?artifact=${reportId}`),
    includeSearchParams: true,
  })
}
